import { ROLES as R } from "../core/roles";
import { lud, only, rule } from "../core/rule";
import { SCOPES } from "../core/scope";

const VIEWERS = lud(R.FA, R.AM, R.DM, R.DMM, R.MENU_SP, R.SALES, R.STORE_L, R.STORE, R.NEW);
const restaurant = (roles) => rule(roles, { scope: SCOPES.RESTAURANT });

export const restaurantPermissions = {
  view: "restaurant.view",
  create: "restaurant.create",
  createOwn: "restaurant.createOwn",
  export: "restaurant.export",
  delete: "restaurant.delete",
  joiningRequirementsExport: "restaurant.joiningRequirements.export",
  onboardingContinue: "restaurant.onboarding.continue",
  appsView: "restaurant.apps.view",
  status: {
    filter: "restaurant.status.filter",
    update: "restaurant.status.update",
  },
  details: {
    open: "restaurant.details.open",
    update: "restaurant.details.update",
  },
  userInfo: {
    update: "restaurant.userInfo.update",
    delete: "restaurant.userInfo.delete",
  },
  app: { manage: "restaurant.app.manage" },
  branch: {
    create: "restaurant.branch.create",
    update: "restaurant.branch.update",
    delete: "restaurant.branch.delete",
    statusUpdate: "restaurant.branch.status.update",
    detailsOpen: "restaurant.branch.details.open",
    phoneOpen: "restaurant.branch.phone.open",
    workTimeOpen: "restaurant.branch.workTime.open",
    workTimeUpdate: "restaurant.branch.workTime.update",
    workTimeDelete: "restaurant.branch.workTime.delete",
    appUserCreate: "restaurant.branch.appUser.create",
    appUserUpdate: "restaurant.branch.appUser.update",
    appUserStatusUpdate: "restaurant.branch.appUser.statusUpdate",
    prepare: "restaurant.branch.prepare",
    userManage: "restaurant.branch.user.manage",
  },
};

export const restaurantGrants = {
  "restaurant.view": rule(VIEWERS),
  // creating a restaurant record directly (backend: POST /stores, ceo-level only; clients use onboarding)
  "restaurant.create": rule(lud()),
  // an existing customer adds another draft store to their OWN account (backend: POST /api/auth/stores/mine). Account level, not scoped:
  // the new store does not exist yet. `new` (still being set up) and branch users cannot.
  "restaurant.createOwn": rule(only(R.STORE, R.STORE_L)),
  "restaurant.export": rule(lud()),
  "restaurant.delete": restaurant(lud()),
  "restaurant.joiningRequirements.export": restaurant(lud(R.AM)),
  // store / storeL see the "continue onboarding" shortcut on their own row.
  "restaurant.onboarding.continue": restaurant(only(R.STORE, R.STORE_L)),
  // active-app icons are hidden from the restaurant itself.
  "restaurant.apps.view": rule(lud(R.FA, R.AM, R.DM, R.DMM, R.MENU_SP, R.SALES)),
  "restaurant.status.filter": rule(lud(R.AM, R.DMM)),
  "restaurant.status.update": restaurant(lud(R.DMM)),
  // fa and menuSp cannot open the business details of a row.
  "restaurant.details.open": rule(lud(R.AM, R.DM, R.DMM, R.SALES, R.STORE, R.STORE_L, R.NEW)),
  "restaurant.details.update": restaurant(lud(R.AM, R.DMM)),
  "restaurant.userInfo.update": restaurant(lud(R.AM, R.DMM)),
  "restaurant.userInfo.delete": restaurant(lud(R.DMM)),
  "restaurant.app.manage": restaurant(lud(R.AM, R.DMM, R.STORE_L)),
  "restaurant.branch.create": restaurant(lud(R.AM, R.DMM)),
  "restaurant.branch.update": restaurant(lud(R.AM, R.DMM)),
  "restaurant.branch.delete": restaurant(lud(R.DMM)),
  "restaurant.branch.status.update": restaurant(lud(R.DMM)),
  "restaurant.branch.details.open": rule(lud(R.AM, R.DM, R.DMM, R.FA)),
  // dm / fa / menuSp see the phone as plain text.
  "restaurant.branch.phone.open": rule(lud(R.AM, R.DMM, R.SALES, R.STORE, R.STORE_L, R.NEW)),
  // menuSp sees the city as plain text (no work-time drawer).
  "restaurant.branch.workTime.open": rule(lud(R.FA, R.AM, R.DM, R.DMM, R.SALES, R.STORE, R.STORE_L, R.NEW)),
  "restaurant.branch.workTime.update": restaurant(lud(R.DM, R.STORE_L, R.DMM, R.AM)),
  "restaurant.branch.workTime.delete": restaurant(lud(R.STORE_L, R.DMM, R.AM)),
  // Preparing a DRAFT branch of the client's own store (details, location, working hours, license) and its branch users (`role: branch`
  // accounts: create + invite, select an existing one, assign / unassign). Preparation never activates anything and needs no subscription;
  // operational activation keeps its own rules (entitlement). Held by the client owners only; staff use the existing administrative flows.
  "restaurant.branch.prepare": restaurant(only(R.NEW, R.STORE, R.STORE_L)),
  "restaurant.branch.user.manage": restaurant(only(R.NEW, R.STORE, R.STORE_L)),
  "restaurant.branch.appUser.create": restaurant(lud(R.AM, R.DMM, R.STORE_L)),
  "restaurant.branch.appUser.update": restaurant(lud(R.AM, R.DMM)),
  // everyone except fa can change an app user's status.
  "restaurant.branch.appUser.statusUpdate": restaurant(
    lud(R.AM, R.DM, R.DMM, R.MENU_SP, R.SALES, R.STORE, R.STORE_L),
  ),
};
